import type { AnalysisFailureReasonCode } from './types';

/**
 * The single source of the copy shown for a document whose contents could not be read.
 *
 * It lives here, not at either call site, because two surfaces render it — the badge on
 * the Documents tab and the callout on the AI review screen — and they must not drift
 * into telling a lawyer two different things about the same file.
 *
 * The copy is written from the reason CODE, never from the backend's `failureReason`
 * string. That string is the raw provider message (`400 The document has no pages` is
 * the real one behind the password-protected case) and it is both meaningless to a
 * lawyer and capable of echoing provider internals, so it deliberately never leaves the
 * backend.
 */
export interface UnreadableDocumentCopy {
  /** Terse label for a badge. */
  label: string;
  /** One sentence naming the cause and what to do about it. */
  detail: string;
}

const COPY: Record<AnalysisFailureReasonCode, UnreadableDocumentCopy> = {
  DOCUMENT_PASSWORD_PROTECTED: {
    label: "Couldn't be read",
    detail:
      'This document is password-protected, so Vale could not read its contents and nothing was extracted from it. Upload an unlocked copy to have it analysed.'
  },
  DOCUMENT_UNREADABLE: {
    label: "Couldn't be read",
    detail:
      'Vale could not read this document, so nothing was extracted from it. The file may be damaged or in a format we cannot open. Try uploading it again, or upload a different copy.'
  }
};

// Both codes share one label today, deliberately: the badge answers "is anything wrong
// with this document", and the cause belongs in the detail the lawyer reads next. The
// map is still keyed per code so a future cause can carry its own label without the
// call sites changing.
const FALLBACK: UnreadableDocumentCopy = COPY.DOCUMENT_UNREADABLE;

/**
 * Copy for an unreadable document. Falls back to the generic wording for a reason code
 * this build does not know — the backend can ship a new cause before the frontend does,
 * and a document silently losing its flag is the exact defect this feature exists to
 * fix.
 */
export function getUnreadableDocumentCopy(
  reasonCode: AnalysisFailureReasonCode | string
): UnreadableDocumentCopy {
  return COPY[reasonCode as AnalysisFailureReasonCode] ?? FALLBACK;
}
